import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { KeyRound, Plug, Server } from 'lucide-react';
import { Button } from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';
import { useOpsCdnConnections, useOpsServices, type OpsServiceRow } from '@/api/operations';
import { opsServiceColumns } from '@/pages/operations/components/ops-service-columns';
import { OpsRavenRebindDialog } from './ops-raven-rebind-dialog';
import { OpsEagleConnectDialog } from './ops-eagle-connect-dialog';

const isRaven = (r: OpsServiceRow) => r.provider?.name?.toLowerCase() === 'raven';
const isEagle = (r: OpsServiceRow) => r.provider?.name?.toLowerCase() === 'eagle';

/**
 * Org detail Services tab — reuses the cross-org services query filtered by
 * org_id. Rows link to /operations/services/:id (the full service detail page).
 * No pagination — orgs with very large service counts will be addressed if
 * needed; at current scale a single page of 100 is sufficient.
 *
 * Raven rows get a "Re-bind credential" action (Raven credentials are shared at
 * org level; the backend enforces the Tech role). Every Eagle row gets "Connect"
 * (still on the shared reseller key) or "Check" (connected: does its key see the
 * service's domains?), and an org with services on the shared key gets one button
 * to connect them all, each against its own Flashcone customer.
 */
export function OpsOrgServicesTab({ orgId }: { orgId: string }) {
  const navigate = useNavigate();
  const [rebindTarget, setRebindTarget] = useState<OpsServiceRow | null>(null);
  const [connectOpen, setConnectOpen] = useState(false);
  const [connectFocus, setConnectFocus] = useState<string | undefined>(undefined);

  const { data, isLoading, isError, refetch } = useOpsServices({
    org_id: Number(orgId),
    per_page: 100,
  });

  const rows = useMemo(() => data?.results ?? [], [data]);
  const ravenRows = useMemo(() => rows.filter(isRaven), [rows]);

  // Connection state of the org's Eagle services (the fleet list, narrowed to this org).
  const connections = useOpsCdnConnections('');
  const eagleConnections = useMemo(
    () => (connections.data ?? []).filter((c) => c.organization_id === orgId),
    [connections.data, orgId],
  );
  const disconnected = useMemo(
    () => eagleConnections.filter((c) => !c.connected && c.status !== 'terminated'),
    [eagleConnections],
  );
  const disconnectedIds = useMemo(() => new Set(disconnected.map((c) => c.service_id)), [disconnected]);
  const eagleIds = useMemo(() => new Set(eagleConnections.map((c) => c.service_id)), [eagleConnections]);

  const columns = useMemo<ColumnDef<OpsServiceRow, unknown>[]>(
    () => [
      ...opsServiceColumns,
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          if (isRaven(row.original)) {
            return (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  setRebindTarget(row.original);
                }}
              >
                <KeyRound size={14} /> Re-bind
              </Button>
            );
          }
          if (isEagle(row.original) && eagleIds.has(row.original.id)) {
            const connected = !disconnectedIds.has(row.original.id);
            return (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  setConnectFocus(row.original.id);
                  setConnectOpen(true);
                }}
              >
                <Plug size={14} /> {connected ? 'Check' : 'Connect'}
              </Button>
            );
          }
          return null;
        },
      },
    ],
    [disconnectedIds, eagleIds],
  );

  if (isError) return <TabError title="Failed to load services" onRetry={() => refetch()} />;
  if (isLoading) return <TabLoading rows={6} />;

  return (
    <>
      {disconnected.length > 0 && (
        <div style={{ marginBottom: 'var(--vn-space-3)' }}>
        <Alert tone="warning">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--vn-space-3)', flexWrap: 'wrap' }}>
            <span>
              {disconnected.length} Eagle service{disconnected.length === 1 ? '' : 's'} of this org still use the
              shared reseller key, so certificates and other changes on their domains are rejected.
            </span>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setConnectFocus(undefined);
                setConnectOpen(true);
              }}
            >
              <Plug size={14} /> Connect Eagle accounts
            </Button>
          </div>
        </Alert>
        </div>
      )}
      <DataTable
        columns={columns}
        data={rows}
        getRowId={(r) => r.id}
        onRowClick={(row) => navigate(`/operations/services/${row.id}`)}
        emptyState={
          <EmptyState
            icon={<Server size={24} />}
            title="No services"
            description="This organization has no provisioned services."
          />
        }
      />
      {connectOpen && (
        <OpsEagleConnectDialog
          key={connectFocus ?? 'all'}
          orgId={orgId}
          services={eagleConnections}
          focusId={connectFocus}
          open
          onOpenChange={(o) => { if (!o) setConnectOpen(false); }}
        />
      )}
      {rebindTarget && (
        <OpsRavenRebindDialog
          key={rebindTarget.id}
          service={rebindTarget}
          ravenServices={ravenRows}
          open
          onOpenChange={(o) => { if (!o) setRebindTarget(null); }}
        />
      )}
    </>
  );
}
